import React, { Fragment, useCallback, useEffect, useState } from "react"
import { CustomerDetail as cd, Customernterface } from "../../services/authinterface"
import { request } from "../../services/request"
import { toast, ToastContainer } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import demo_image from "../../assets/images/demo_user.png"
import moment from "moment"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import SecondaryButton from "../../components/subComponents/SecondaryButton"
import NodataImg from "../../assets/images/NoDataFound.png"
import { skipNull, skipNullWithDash } from "../../utils/utils"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { FaChevronLeft } from "react-icons/fa"
import ConfirmModal from "../../components/common/ConfirmModal"

const Icon: any = FaChevronLeft

type VerificationType = "police" | "education"

const getPendingVerifications = (customer: cd): VerificationType[] => {
  const pending: VerificationType[] = []
  if (customer.is_police && !customer.is_police_verified) pending.push("police")
  if (customer.is_school && !customer.is_school_verified) pending.push("education")
  return pending
}

const CustomerDetail: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const param = useParams()
  const [customers, setCustomers] = useState<cd[]>([])
  const [verifyingType, setVerifyingType] = useState<VerificationType | null>(null)
  const [updatingSuperUser, setUpdatingSuperUser] = useState(false)
  const [confirmSuperUser, setConfirmSuperUser] = useState(false)

  const fetchCustomerDetails = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))
      const response = await request<Customernterface>({
        url: `/adminCustomers/viewCustomer/${param.id}`,
        method: "GET",
      })
      if (response.data) {
        setCustomers(response.data)
      } else {
        toast.error("Failed to fetch customers: " + response.message)
      }
    } catch (error) {
      toast.error("Something went wrong")
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, param])

  const handleAddPaymentDetails = async (id: any) => {
    try {
      const response = await request({
        url: `/payment/newsetup/${id}`,
        method: "POST",
        // headers: { apikey: process.env.REACT_APP_API_KEY || "gunsens-api" }
      })
      if (response.data) {
        const redirectUrl = response?.redirectUrl || response?.data?.redirectUrl
        if (redirectUrl) {
          window.open(redirectUrl, "_blank")
        }
        fetchCustomerDetails()
        // toast.success(response.message)
      } else {
        toast.error(response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    }
  }

  const handleApproveVerification = async (userAccountId: string, type: VerificationType) => {
    const label = type === "police" ? "police" : "school"
    if (!window.confirm(`Are you sure you want to verify this ${label} account?`)) return
    setVerifyingType(type)
    try {
      const response = await request({
        url: `/adminCustomers/approveUserVerification/${userAccountId}`,
        method: "POST",
        data: { type },
      })
      toast.success(response?.message || "Verification approved")
      fetchCustomerDetails()
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      setVerifyingType(null)
    }
  }

  // The flag belongs to the person, so every account row carries the same value.
  const updateSuperUser = async (customer: cd, nextValue: boolean) => {
    setUpdatingSuperUser(true)
    try {
      const response = await request({
        url: `/adminCustomers/updateSuperUser/${customer.userId}`,
        method: "POST",
        data: { is_super_user: nextValue },
      })
      const updatedValue = response?.data?.is_super_user ?? nextValue
      setCustomers((prev) => prev.map((row) => ({ ...row, is_super_user: updatedValue })))
      toast.success(response?.message || "Super user flag updated")
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      setUpdatingSuperUser(false)
      setConfirmSuperUser(false)
    }
  }

  // Turning it on opens the confirm modal, turning it off goes straight through.
  const handleToggleSuperUser = (customer: cd) => {
    if (customer.is_super_user) {
      updateSuperUser(customer, false)
    } else {
      setConfirmSuperUser(true)
    }
  }

  useEffect(() => {
    fetchCustomerDetails()
  }, [fetchCustomerDetails])

  const getBack = () => {
    navigate(-1)
  }

  return (
    <section className='dashboard-section'>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />
      <ConfirmModal
        show={confirmSuperUser}
        title='Make Super User'
        message={
          <>
            Make{" "}
            <strong>
              {skipNull(customers[0]?.first_name)} {skipNull(customers[0]?.last_name)}
            </strong>{" "}
            a super user? They will be able to delete any comment in the app.
          </>
        }
        confirmLabel='Confirm'
        loadingLabel='Updating...'
        isLoading={updatingSuperUser}
        onConfirm={() => updateSuperUser(customers[0], true)}
        onClose={() => setConfirmSuperUser(false)}
      />

      <div className='container'>
        <div className='row'>
          <div className='col-12 mb-3'>
            <div className='d-flex gap-1 customer-detail-line'>
              <button onClick={getBack} className='d-flex align-items-center'>
                <Icon size={20} />
              </button>
              <div className='dashboard-table-title-section'>
                <h2>Customer Details</h2>
              </div>
            </div>

            <div className='col-12 mt-3'>
              <div className='row justify-content-center'>
                <div className='col-sm-9 col-md-6 col-lg-5 col-xl-5'>
                  <div className='profileDetails-main-section'>
                    {customers[0] && (
                      <Fragment>
                        {customers[0].role !== "0" ? (
                          <div className='owner-main-section'>
                            <h4 className='owner-details-title'>Owner Details</h4>
                            <div className='owner-details-section'>
                              <div className='profile-img'>
                                <img
                                  src={
                                    customers[0]?.owner.profile_image == null
                                      ? demo_image
                                      : customers[0]?.owner.profile_image
                                  }
                                  alt='owner'
                                />
                              </div>
                              <div className=''>
                                <h3 className='customerProfile-card-title'>
                                  {skipNull(customers[0]?.owner.first_name) +
                                    " " +
                                    skipNullWithDash(customers[0]?.owner.last_name)}
                                </h3>
                                <a
                                  href={`mailto:${customers[0].owner.email}`}
                                  className='customerProfile-card-link'
                                >
                                  {customers[0].owner.email}
                                </a>
                                {/* <span className='customerProfile-card-budge '>Owner</span> */}
                              </div>
                            </div>
                          </div>
                        ) : (
                          ""
                        )}

                        <div className='dashboard-card customerProfile-card'>
                          <div className='dashboard-card-header'>
                            <div className='profile-img'>
                              <img
                                src={
                                  customers[0]?.acount_profile_image == null
                                    ? demo_image
                                    : customers[0]?.acount_profile_image
                                }
                                alt='Customer'
                              />
                            </div>
                          </div>
                          <div className='customerProfile-card-details'>
                            <div className='d-flex align-items-center justify-content-between mb-1'>
                              <h3 className='customerProfile-card-title'>
                                {skipNull(customers[0]?.first_name) +
                                  " " +
                                  skipNullWithDash(customers[0]?.last_name)}
                              </h3>
                              <div className='customerProfile-budge-section'>
                                {customers[0].is_police === true && (
                                  <span className='customerProfile-card-budge'>Police</span>
                                )}
                                {customers[0].is_school === true && (
                                  <span className='customerProfile-card-budge'>School</span>
                                )}

                                <span className='customerProfile-card-budge'>
                                  {customers[0].role === "0"
                                    ? "Owner"
                                    : customers[0].role === "1"
                                      ? "Admin"
                                      : customers[0].role === "2"
                                        ? "Viewer"
                                        : customers[0].role === "3"
                                          ? "Kids"
                                          : ""}
                                </span>
                              </div>
                            </div>
                            <a
                              className='customerProfile-card-link'
                              href={`mailto:${customers[0].email}`}
                            >
                              {customers[0].email}
                            </a>
                            <p className='customerProfile-card-text'>
                              <span>Joined :</span>{" "}
                              {moment(customers[0].createdAt).format("MMM-DD-YYYY HH:mm")}
                            </p>

                            {customers[0].notes && (
                              <p className='customerProfile-card-text'>
                                <span>Notes:</span>
                                {customers[0].notes}
                              </p>
                            )}
                            <div>
                              <p className='customerProfile-card-text'>
                                <span>SOS Status :</span>

                                {customers[0].SOS_Status === "1" ? (
                                  <span className='active'>Active</span>
                                ) : customers[0].SOS_Status === "9" ? (
                                  <span className='Inactive'>Inactive</span>
                                ) : (
                                  ""
                                )}
                              </p>
                              {customers[0].owner.BillInfoDueDate != null && (
                                <p className='customerProfile-card-text date-section'>
                                  SOS Bill Due Date :
                                  <span className='customerProfile-card-budge'>
                                    {moment(customers[0].owner.BillInfoDueDate).format(
                                      "MMM-DD-YYYY",
                                    )}
                                  </span>
                                </p>
                              )}
                            </div>

                            <p className='customerProfile-card-text'>
                              Sensor Status:{" "}
                              <span className='customerProfile-sos-status'>
                                {customers[0].Sensor_Status === "1" ? (
                                  <span className='active'>Active</span>
                                ) : customers[0].Sensor_Status === "9" ? (
                                  <span className='Inactive'>Inactive</span>
                                ) : (
                                  ""
                                )}
                              </span>
                            </p>
                            <p className='customerProfile-card-text date-section'>
                              {customers[0].owner.BillInfoDueDateSensor != null && (
                                <>
                                  Sensor Bill Due Date
                                  <span className='customerProfile-card-budge'>
                                    {moment(customers[0].owner.BillInfoDueDateSensor).format(
                                      "MMM-DD-YYYY",
                                    )}
                                  </span>
                                </>
                              )}
                            </p>
                            {customers[0]?.userAccounts?.UsersEmergencyContacts &&
                              customers[0]?.userAccounts?.UsersEmergencyContacts?.length > 0 && (
                                <>
                                  <div className='emergencyContacts-details-section'>
                                    <h3>Emergency Contacts :</h3>
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>Type</th>
                                          <th>Name</th>
                                          <th>Email</th>
                                          <th>App User</th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {customers[0]?.userAccounts?.UsersEmergencyContacts.map(
                                          (UsersEmergencyContact: any) => (
                                            <tr key={UsersEmergencyContact.id}>
                                              <td className='customersType'>
                                                {UsersEmergencyContact.type}
                                              </td>

                                              <td className='customersName'>
                                                {UsersEmergencyContact.firstName}
                                              </td>
                                              <td>
                                                <a href={`mailto:${UsersEmergencyContact.email}`}>
                                                  {UsersEmergencyContact.email}
                                                </a>
                                              </td>
                                              <td>
                                                {UsersEmergencyContact.isAppUser === "0"
                                                  ? "No"
                                                  : "Yes"}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}
                            {getPendingVerifications(customers[0]).length > 0 && (
                              <div className='verification-details-section'>
                                <h3>Pending Verification :</h3>
                                {customers[0].accountDocument?.accountDocument_image ? (
                                  <a
                                    href={customers[0].accountDocument.accountDocument_image}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='verification-document'
                                  >
                                    {/\.pdf($|\?)/i.test(
                                      customers[0].accountDocument.accountDocument_image,
                                    ) ? (
                                      <span className='customerProfile-sos-status'>View Document (PDF)</span>
                                    ) : (
                                      <img
                                        src={customers[0].accountDocument.accountDocument_image}
                                        alt='Verification document'
                                      />
                                    )}
                                  </a>
                                ) : (
                                  <p className='customerProfile-card-text'>No document uploaded.</p>
                                )}
                                <div className='verification-button-section'>
                                  {getPendingVerifications(customers[0]).map((type) => (
                                    <PrimaryButton
                                      key={type}
                                      label={
                                        verifyingType === type
                                          ? "Verifying..."
                                          : type === "police"
                                            ? "Verify Police"
                                            : "Verify School"
                                      }
                                      className='btns'
                                      disabled={verifyingType !== null}
                                      onClick={() => handleApproveVerification(customers[0].id, type)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className='customerProfile-card-text d-flex align-items-center gap-2'>
                              <span>Super user :</span>
                              <div className='toggle-container'>
                                <label className='switch'>
                                  <input
                                    type='checkbox'
                                    aria-label='Super user'
                                    checked={!!customers[0].is_super_user}
                                    disabled={updatingSuperUser}
                                    onChange={() => handleToggleSuperUser(customers[0])}
                                  />
                                  <span className='slider' />
                                </label>
                              </div>
                            </div>
                            {customers[0].owner.paymentUpdated === 0 ? <p className='customerProfile-card-text'>
                              Payment Detail:{" "}
                              {/* <a target="_blank" rel="noreferrer" className="customerProfile-sos-status" href={`${process.env.REACT_APP_API_BASE_URL}/payment/newsetup/${customers?.[0]?.owner?.id}`}>
                                Add Payment Details
                              </a> */}
                              <button rel="noreferrer" className="customerProfile-sos-status" onClick={() => handleAddPaymentDetails(customers?.[0]?.owner?.id)}>
                                Add Payment Details
                              </button>
                            </p> : ""}
                            <div className='customerProfile-card-button-section'>
                              <SecondaryButton label='Back' className='btns' onClick={getBack} />
                              <PrimaryButton
                                label='Edit'
                                className='btns'
                                to={`/customer/edit/${customers[0].id}`}
                              />

                              <PrimaryButton
                                label='View Bills'
                                className='btns'
                                to={`/customer/bills/${customers[0].owner.id}`}
                              />

                              <PrimaryButton
                                label='Billing'
                                className='btns'
                                to={`/customer/billing/${customers[0].owner.id}`}
                              />
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </div>
                  {customers.length === 0 && (
                    <div className='noDatafound-section'>
                      <div className='noDatafound-img'>
                        <img src={NodataImg} alt='' />
                      </div>
                      <h2 className='noDatafound-text'>No customer details found.</h2>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CustomerDetail
